<template>
	<Row v-if="coordsCorrectionsHelpKey">
		<Col offset="4" span="20">
			<TypographyText type="secondary">
				{{ $t('base-stations-edit.fields.coordsCorrectionsHelp.' + coordsCorrectionsHelpKey) }}
				<TypographyText v-if="coordsCorrectionsHelpKey === 'one'" type="secondary" code>
					{{ baseStationDetailsRef.rinexStart }}
				</TypographyText>
				<span v-else-if="coordsCorrectionsHelpKey === 'period'">
					<TypographyText type="secondary" code>{{ baseStationDetailsRef.rinexStart }}</TypographyText>
					&ndash;
					<TypographyText type="secondary" code>{{ baseStationDetailsRef.rinexEnd }}</TypographyText>
				</span>
				<div v-if="['one', 'period'].includes(coordsCorrectionsHelpKey)">
					{{ $t('base-stations-edit.fields.coordsCorrectionsHelp.stdDevs') }}
					<TypographyText type="secondary" code>
						{{ formatMilliMetres(baseStationDetailsRef.xStd) }} /
						{{ formatMilliMetres(baseStationDetailsRef.yStd) }} /
						{{ formatMilliMetres(baseStationDetailsRef.zStd) }}
						{{ $t('base-stations-edit.fields.coordsCorrectionsHelp.mm') }}
					</TypographyText>
				</div>
			</TypographyText>
			<div v-if="!baseStationDetailsRef.reliableCoords" class="pd-vertical">
				<VerticalLayout>
					<Tag color="pink">{{ $t('base-stations-edit.fields.coordsCorrectionsHelp.nonReliableCoords') }}</Tag>
					<PopconfirmButton
						v-if="formsChangedRef && setAsReliableEnabled"
						:title="$t('base-stations-edit.fields.coordsCorrectionsHelp.setAsReliableConfirmation')"
						:ok-text="$t('base-stations-edit.fields.coordsCorrectionsHelp.setAsReliableButtonLabel')"
						:cancel-text="$t('base-stations-edit.fields.coordsCorrectionsHelp.setAsReliableCancelButtonLabel')"
						:loading="setAsReliableLoading"
						:onConfirm="() => setAsReliable()"
					>
						{{ $t('base-stations-edit.fields.coordsCorrectionsHelp.setAsReliableButtonLabel') }}
					</PopconfirmButton>
					<Button v-if="!formsChangedRef && setAsReliableEnabled" type="default" @click="setAsReliable" :loading="setAsReliableLoading">
						{{ $t('base-stations-edit.fields.coordsCorrectionsHelp.setAsReliableButtonLabel') }}
					</Button>
				</VerticalLayout>
			</div>
		</Col>
	</Row>
</template>

<script>
import { toRef, computed, ref } from 'vue';

import { useI18n } from 'vue-i18n'

import { Row, Col, TypographyText, Tag, Button, message } from 'ant-design-vue';

import { hasCoords } from '@/utils/baseStationsUtils';
import { showErrorMessage } from '@/components/common/errorMessage';

import Api from '@/api/Api';
import Utils from '@/api/Utils';

import VerticalLayout from '@/components/common/VerticalLayout.vue';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';

export default {
	components: { Row, Col, TypographyText, Tag, Button, VerticalLayout, PopconfirmButton },
	props: ['baseStationDetails', 'allowPaas', 'formState', 'loadBaseStationDetails', 'resetChangedState', 'formsChanged'],
	setup(props) {
		const { t } = useI18n();
		const baseStationDetailsRef = toRef(props, 'baseStationDetails');
		const allowPaasRef = toRef(props, 'allowPaas');
		const coordsCorrectionsHelpKey = computed(() => {
			if (!Utils.isObjectWithData(baseStationDetailsRef.value)) {
				return null;
			}
			if (!hasCoords(baseStationDetailsRef.value)) {
				return allowPaasRef.value ? 'coordsMissing' : 'coordsMissingNoPaas';
			}
			if (baseStationDetailsRef.value.rinexStart == null || baseStationDetailsRef.value.rinexEnd == null) {
				if (baseStationDetailsRef.value.coordsSource === 'unkn') {
					return baseStationDetailsRef.value.reliableCoords ? 'user' : 'srt';
				}
				return baseStationDetailsRef.value.coordsSource;
			}
			if (baseStationDetailsRef.value.rinexStart === baseStationDetailsRef.value.rinexEnd) {
				return 'one';
			}
			return 'period';
		});
		const setAsReliableLoading = ref(false);
		const setAsReliable = () => {
			setAsReliableLoading.value = true;
			Api.baseStationSetReliableCoords(baseStationDetailsRef.value.id, {
				x_coord: Utils.m2mm(props.formState.xCoord),
				y_coord: Utils.m2mm(props.formState.yCoord),
				z_coord: Utils.m2mm(props.formState.zCoord),
				ant_reference_point: props.formState.antReferencePoint,
			}, responseData => {
				setAsReliableLoading.value = false;
				if (responseData.status === 'ok') {
					message.success(t('base-stations-edit.fields.coordsCorrectionsHelp.setAsReliableSuccess'));
					props.resetChangedState();
					props.loadBaseStationDetails();
				} else {
					showErrorMessage(responseData.errors);
				}
			});
		}
		const setAsReliableEnabled = computed(() => {
			return (
				!Utils.valueIsEmpty(props.formState.xCoord) &&
				!Utils.valueIsEmpty(props.formState.yCoord) &&
				!Utils.valueIsEmpty(props.formState.zCoord) &&
				props.formState.antReferencePoint !== 'NA'
			);
		});
		const formsChangedRef = toRef(props, 'formsChanged');
		return {
			baseStationDetailsRef,
			coordsCorrectionsHelpKey,
			formatMilliMetres: Utils.formatMilliMetres,
			setAsReliableLoading,
			setAsReliable,
			setAsReliableEnabled,
			formsChangedRef,
		}
	}
}
</script>
